import React, { useRef, useState } from "react";
import { trackClickedButton } from "utils/analytics";
import { logEducatorDashboard, LogLevel } from "utils";
import { kebabId, setLinkId } from "./WarmUpGroupCard";
import { SetPreview } from "components/pages/WarmUpPicker/SetPreview";
import { ArchiveLessonModal } from "./ArchiveLessonModal";
import { EditWarmUpGroupsModal } from "./EditWarmUpGroupsModal";
import { WarmUpCopyModal } from "./WarmUpCopyModal";
import { archiveWarmUpAssignment, updateWarmUpSessionLength } from "components/pages/WarmUpPicker/warmUpApi";

// The warm-up cards' behaviour on Manage Groups: copy, the set preview, the
// session length PATCH, the card menu, Edit Groups and Archive Groups with
// their dialogs.

const BODY_TAIL =
  "Students can no longer join with them, and the codes become available for new groups. Your session reports are safe and will stay on your dashboard.";

const codesClause = (count) => {
  if (count === 0) return "these warm-up groups";
  return `the ${count} ${count === 1 ? "group code" : "group codes"} of these warm-up groups`;
};

const archiveBody = (assignment) => `You are about to clear ${codesClause(assignment.groups.length)}. ${BODY_TAIL}`;

// A 404 means the assignment is already gone: archived elsewhere, or a 204
// whose retry by `request` found nothing left to delete.
const ALREADY_GONE = 404;

const shareLink = (code) => `${process.env.REACT_APP_OKOLABS_APP_URL}?code=${code}`;

// The same key the list gives the card, so one open-menu id serves every card.
export const warmUpMenuId = (assignment) => `warmUp:${assignment.id}`;

export const useWarmUpCards = ({
  classroomId,
  source,
  refetchWarmUps,
  // Puts a saved assignment in place in the list (no re-sort, no new read).
  replaceAssignment,
  // Reads the class's counts again after an archive, and also after an Edit
  // Groups save.
  onArchived,
  // Asked before the archive confirmation or Edit Groups opens; false keeps
  // the page as is.
  guardArchive = () => true,
  // The one card menu open on the page, shared with the other cards.
  openMenuId,
  setOpenMenuId,
  // Called just before a saved assignment replaces its copy in place; what it
  // returns starts a follow-up read without releasing that order.
  onSavedInPlace,
  // Called once an archive went through, before the list is read again.
  onCardArchived,
  // The account's classes in the active school: Copy to Classes picks from them.
  classes = [],
  // Shows a banner per copy and reads the account again: (copies, setName).
  onCopied,
  // Reads the account again: a copy target turned out to be out of reach.
  onAccountRefresh,
}) => {
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [copyTarget, setCopyTarget] = useState(null);
  const [previewed, setPreviewed] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [archiveFailedId, setArchiveFailedId] = useState(null);
  const archiveInFlight = useRef(false);

  const eventProps = (assignment) => ({
    page_name: "Dashboard",
    class: classroomId,
    source,
    warmup_set: assignment.warmup_set.key,
  });

  // One card menu at a time: opening one closes any other.
  const changeMenu = (assignment, open) => {
    if (open) trackClickedButton("Warm-Up Group Menu", eventProps(assignment));
    const id = warmUpMenuId(assignment);
    setOpenMenuId((current) => {
      if (open) return id;
      return current === id ? null : current;
    });
  };

  const finishArchive = () => {
    archiveInFlight.current = false;
    setArchiving(false);
    setArchiveTarget(null);
  };

  const archived = (assignment) => {
    trackClickedButton("Archive Groups", { ...eventProps(assignment), group_count: assignment.groups.length });
    finishArchive();
    onCardArchived?.(assignment);
    refetchWarmUps?.();
    onArchived?.();
  };

  const archiveFailed = (assignment, error) => {
    logEducatorDashboard("Educator dashboard: Failed to archive warm-up groups", {
      level: LogLevel.WARNING,
      event: "warm_up_archive_error",
      statusCode: error?.status,
      classroomId,
      assignmentId: assignment.id,
    });
    setArchiveFailedId(assignment.id);
    finishArchive();
  };

  const confirmArchive = () => {
    if (archiveInFlight.current || !archiveTarget) return;
    const assignment = archiveTarget;
    archiveInFlight.current = true;
    setArchiving(true);
    setArchiveFailedId(null);
    archiveWarmUpAssignment(classroomId, assignment.id)
      .then(() => archived(assignment))
      .catch((error) => (error?.status === ALREADY_GONE ? archived(assignment) : archiveFailed(assignment, error)));
  };

  const closeArchive = () => {
    if (!archiveInFlight.current) setArchiveTarget(null);
  };

  // Resolves once saved (or once known to be gone: the list is read again);
  // rejects on any other failure, which the card reports.
  const saveSessionLength = (assignment, sessionLength) =>
    updateWarmUpSessionLength(classroomId, assignment.id, sessionLength)
      .then((updated) => {
        onSavedInPlace?.();
        replaceAssignment?.(updated);
        trackClickedButton("Session Length", { ...eventProps(assignment), session_length: sessionLength });
      })
      .catch((error) => {
        if (error?.status === ALREADY_GONE) {
          refetchWarmUps?.();
          return;
        }
        logEducatorDashboard("Educator dashboard: Failed to save warm-up session length", {
          level: LogLevel.WARNING,
          event: "warm_up_session_length_error",
          statusCode: error?.status,
          classroomId,
          assignmentId: assignment.id,
        });
        throw error;
      });

  const openEdit = (assignment) => {
    if (!guardArchive()) return;
    trackClickedButton("Edit Warm-Up Groups", eventProps(assignment));
    setEditTarget(assignment);
  };

  // The modal does not restore focus itself; it goes back to the card's kebab.
  const closeEdit = () => {
    const kebab = kebabId(editTarget.id);
    setEditTarget(null);
    document.getElementById(kebab)?.focus();
  };

  // The card changes in place. Codes went in and out of the class's pool, so
  // the counts are read again, as after an archive; that read keeps the order.
  // The modal closes first, so its render is not taken for the edited one.
  const editSaved = (updated, { addedCount, removedCount }) => {
    trackClickedButton("Save Warm-Up Group Codes", {
      ...eventProps(editTarget),
      group_count: updated.groups.length,
      added_count: addedCount,
      removed_count: removedCount,
    });
    closeEdit();
    const readCountsInPlace = onSavedInPlace?.();
    replaceAssignment?.(updated);
    const readCounts = () => onArchived?.();
    if (readCountsInPlace) readCountsInPlace(readCounts);
    else readCounts();
  };

  // Archived elsewhere (or the class is out of reach): the list is read again
  // and focus moves as after an archive.
  const editGone = () => {
    onCardArchived?.(editTarget);
    setEditTarget(null);
    refetchWarmUps?.();
  };

  const openCopy = (assignment) => {
    if (!guardArchive()) return;
    trackClickedButton("Copy to Classes", eventProps(assignment));
    setCopyTarget(assignment);
  };

  const trackCopy = (targetCount) =>
    trackClickedButton("Copy Warm-Up Groups", {
      ...eventProps(copyTarget),
      target_count: targetCount,
      group_count: copyTarget.groups.length,
    });

  // The modal does not restore focus itself; it goes back to the card's kebab.
  const closeCopy = () => {
    const kebab = kebabId(copyTarget.id);
    setCopyTarget(null);
    document.getElementById(kebab)?.focus();
  };

  const copied = (copies) => {
    trackCopy(copies.length);
    const setName = copyTarget.warmup_set.name;
    closeCopy();
    onCopied?.(copies, setName);
  };

  // Archived elsewhere: the list is read again and focus moves as after an
  // archive, as for Edit Groups.
  const copyGone = () => {
    onCardArchived?.(copyTarget);
    setCopyTarget(null);
    refetchWarmUps?.();
  };

  const openSet = (assignment) => {
    trackClickedButton("Warm-Up Set", eventProps(assignment));
    setPreviewed(assignment);
  };

  // The preview does not restore focus itself; it goes back to the link.
  const closeSet = () => {
    const linkId = setLinkId(previewed.id);
    setPreviewed(null);
    document.getElementById(linkId)?.focus();
  };

  const copyCode = (assignment, code) => {
    trackClickedButton("Copy Code", { ...eventProps(assignment), group_code: code, card_type: "Warm-Up Group" });
    const write = navigator.clipboard
      ? navigator.clipboard.writeText(shareLink(code))
      : Promise.reject(new Error("Clipboard unavailable"));
    return write.catch((error) => {
      logEducatorDashboard("Educator dashboard: Warm-up group code link not copied", {
        level: LogLevel.WARNING,
        event: "warm_up_code_copy_error",
        groupCode: code,
        errorName: error?.name,
      });
      throw error;
    });
  };

  const cardProps = (assignment) => ({
    assignment,
    onCopyCode: (code) => copyCode(assignment, code),
    onSessionLengthChange: (value) => saveSessionLength(assignment, value),
    onOpenSet: () => openSet(assignment),
    menuOpen: openMenuId === warmUpMenuId(assignment),
    onMenuOpenChange: (open) => changeMenu(assignment, open),
    onCopyToClasses: () => openCopy(assignment),
    onEdit: () => openEdit(assignment),
    onArchive: () => {
      if (guardArchive()) setArchiveTarget(assignment);
    },
    archiveFailed: archiveFailedId === assignment.id,
  });

  const dialogs = (
    <>
      {previewed && <SetPreview set={previewed.warmup_set} onClose={closeSet} />}
      {copyTarget && (
        <WarmUpCopyModal
          assignment={copyTarget}
          classroomId={classroomId}
          classes={classes}
          onClose={closeCopy}
          onCopied={copied}
          onGone={copyGone}
          onAccountRefresh={onAccountRefresh}
        />
      )}
      {editTarget && (
        <EditWarmUpGroupsModal
          assignment={editTarget}
          classroomId={classroomId}
          onClose={closeEdit}
          onSaved={editSaved}
          onGone={editGone}
        />
      )}
      <ArchiveLessonModal
        isOpen={Boolean(archiveTarget)}
        onClose={closeArchive}
        onConfirm={confirmArchive}
        archiving={archiving}
        title="Ready to archive?"
        body={archiveTarget ? archiveBody(archiveTarget) : ""}
        keepLabel="Keep Groups"
        confirmLabel="Yes, Archive"
        titleId="archive-warm-up-groups-title"
      />
    </>
  );

  return { cardProps, dialogs };
};
